import states from './states.json';
import haversine from 'haversine';
import mapboxgl from 'mapbox';
import Awesomplete from 'awesomplete';
import debounce from '../../../assets/js/base/_debounce.js';
import geo from '../../../assets/js/base/_geolocate.js';

function clearNode( node ) {
	while ( node.firstChild ) {
		node.removeChild( node.firstChild );
	}
}

function sortResultsByDistance(a, b) {
	return a.distance === b.distance ? 0 : a.distance > b.distance ? 1 : -1
}

function getDistance( distance ) {
	const fixedDistance = parseFloat( distance.toFixed(1) );
	const label = 1 === fixedDistance ? 'mile' : 'miles';

	return `${fixedDistance} ${label}`;
}


const statesNameMap = states.reduce( ( map, state ) => ( { ...map, [ state.name.toLowerCase() ]: state } ), {} );
const statesAbbrMap = states.reduce( ( map, state ) => ( { ...map, [ state.abbr.toLowerCase() ]: state } ), {} );

class MemberLocator {

	constructor( node, config = { data: [], attributes: {} } ) {
		this.node    = node;
		this.results = {
			primary: [],
			local: [],
		};

		this.config = {
			data: [ ...config.data || [] ],
			attributes: {},
		};

		this.data       = [ ...( config.data || [] ) ];
		this.attributes = { ...config.attributes };
		this.form       = this.node.querySelector('.member-locator__search');
		this.input      = this.form.querySelector('input');
		this.datalist   = this.form.querySelector('datalist');
		this.items      = this.node.querySelector('.member-locator__items');
		this.map        = this.node.querySelector('.member-locator__map');
		this.api        = 'https://api.mapbox.com/geocoding/v5/mapbox.places/';
		this.searchType = null;
		this.stateData  = null;

		this.awesomplete = new Awesomplete( this.input );
		this.input.addEventListener('focus', () => this.awesomplete.open());

		this.input.addEventListener('awesomplete-select', (e) => {
			if ( e.text.value ) {
				this.submit();
			}
		});

		this.mapboxParams = {
			autocomplete: true,
			fuzzyMatch: true,
			limit: 10,
			access_token: mapboxgl.accessToken,
			country: 'US,AS,GU,MP,PR,VI,UM',
			types: 'region,postcode,district,place,address',
		};

		this.hashMap = {};
		this.data.forEach( result => this.hashMap[ result.id ] = result );

		this.zipMap = {};
		this.data.forEach(( result ) => {
			result.serviced_zip.forEach(( zip ) => {
				if ( ! this.zipMap[ zip ] ) {
					this.zipMap[ zip ] = [];
				}

				this.zipMap[ zip ].push( result.id );
			});
		});

		this.mapbox = new mapboxgl.Map({
			container: this.map,
			style: 'mapbox://styles/mapbox/streets-v11',
			center: [ -77.0675422, 38.8023649 ], // default to national hq
			zoom: 10,
		});

		this.mapbox.on('load', () => {
			// @see https://docs.mapbox.com/mapbox-gl-js/example/geojson-markers/

			const source = {
				type: 'geojson',
				data: {
					type: 'FeatureCollection',
					features: this.data.map( ( { longitude, latitude, ...props } ) => ( {
						type: 'Feature',
						geometry: {
							type: 'Point',
							coordinates: [ longitude, latitude ]
						},
						properties: {
							...props
						}
					} ) ),
				}
			}

			this.mapbox.loadImage( '/wp-content/themes/ccusa/assets/img/map-marker.png', ( error, image ) => {
				this.mapbox.addImage( 'ccusa-marker', image );
				this.mapbox.addSource( 'members', source );
				this.mapbox.addLayer({
					id: 'points',
					type: 'symbol',
					source: 'members',
					layout: {
						'icon-image': 'ccusa-marker',
						'icon-size': 0.75,
						'icon-anchor': 'bottom',
						// 'text-field': [ 'get', 'title' ],
						'text-anchor': 'top',
					}
				});
			});

			this.mapbox.addControl( new mapboxgl.NavigationControl(), 'bottom-right' );

			this.mapbox.on('click', 'points', (e) => {
				const feature = e.features[0];

				new mapboxgl.Popup({
					anchor: 'top',
				}).setLngLat( feature.geometry.coordinates )
					.setHTML( `
						<h3 class="has-h6-variant">
							<a href="${ feature.properties.link }" target="_blank">
								${ feature.properties.title ?? '' }
							</a>
						</h3>
						<p><small>${ getDistance( feature.properties.distance ) }</p>
						<p><small>${ feature.properties.formatted_address }</p>
						`
					)
					.addTo( this.mapbox );
			});
		});

		this.input.addEventListener( 'keyup', debounce( (e) => {
			if ( ! [ 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight' ].includes( e.key ) && this.input.value && this.input.value.length > 3 ) {
				this.autocomplete( this.input.value );
			}
		}, 300 ) );

		this.form.addEventListener( 'submit', (e) => {
			e.preventDefault();
			this.submit();
		});

		this.node.classList.add( 'is-loading' );

		geo.locate().then((geo) => {
			this.geo = geo;
			this.render( true );
		}).catch((err) => {
			console.error( err )
		}).finally( () => {
			this.node.classList.remove('is-loading');
		});
	}

	render( firstRun = false ) {
		clearNode( this.items );

		this.results = this.data.reduce(
			( results, result, index ) => {
				result.distance = haversine(
					{ latitude: result.latitude, longitude: result.longitude },
					{ latitude: this.geo.latitude, longitude: this.geo.longitude },
					{ unit: 'mile' }
				);

				if ( 'state' === this.searchType ) {
					const state     = result.state.trim().toLowerCase();
					const stateAbbr = result.state_name.trim().toLowerCase();
					const search    = this.input.value.trim().toLowerCase();

					if ( state === search || stateAbbr === search ) {
						results.local.push( result );
					}
				} else {
					if ( this.geo.zip_code && this.zipMap[ this.geo.zip_code ] && this.zipMap[ this.geo.zip_code ].includes( result.id ) ) {
						results.primary.push( result );
					} else {
						if ( result.distance <= this.attributes.maxDistance ) {
							results.local.push( result );
						}
					}
				}

				return results;
			},
			{
				primary: [],
				local: [],
			}
		);

		let primaryNotice;

		if ( this.results.primary.length ) {
			const heading = document.createElement('h2');
			heading.classList.add('has-h3-variant');
			heading.appendChild( document.createTextNode( this.attributes.heading ) );
			this.items.appendChild( heading );

			if ( firstRun ) {
				if ( this.results.primary.length === 1 ) {
					primaryNotice = this.attributes.subheadingSingular;
				} else {
					primaryNotice = this.attributes.subheading;
				}
			} else if ( this.geo.zip_code && 'zip' === this.searchType ) {
				primaryNotice = `Results for zip code: ${ this.geo.zip_code }`;
			} else {
				// primaryNotice = `Results for: ${ this.input.value }`;
				primaryNotice = this.attributes.searchNotice.replace( /{{search}}/g, this.input.value );
			}
		} else if ( this.results.local.length ) {
			primaryNotice = this.attributes.noMatchesNotice;
			// primaryNotice = `While there is no Catholic Charities agency operating in your ZIP code, you may find assistance at the ${ this.results.local.length > 1 ? 'agencies' : 'agency' } listed below.`;
		} else {
			primaryNotice = this.attributes.searchNotice.replace( /{{search}}/g, this.input.value ); // `Results for: ${ this.input.value }:`;
		}

		if ( primaryNotice ) {
			this.items.appendChild( this.renderNotice( primaryNotice ) );
		}

		if ( this.results.primary.length ) {
			this.results.primary.sort( sortResultsByDistance );
			this.results.primary.forEach( ( result ) => this.items.appendChild( this.renderResult( result, 'member-locator__result--match' ) ) );
		}

		if ( this.results.local.length ) {
			const noticeText = this.results.local.length === 1 ? this.attributes.localAgenciesNoticeSingular : this.attributes.localAgenciesNotice;

			this.items.appendChild( this.renderNotice( noticeText.replace( /{{count}}/g, this.results.local.length ).replace( /{{maxDistance}}/g, this.attributes.maxDistance ) ) );

			this.results.local.sort( sortResultsByDistance );
			this.results.local.forEach( ( result ) => this.items.appendChild( this.renderResult( result ) ) );
		}

		const fitBoundsOpts = { padding: 100 };

		if ( firstRun ) {
			fitBoundsOpts.animate = false;
		}

		if ( 'state' === this.searchType && this.stateData ) {
			this.mapbox.fitBounds( this.stateData.bbox, { padding: 10 } );
		} else {
			if ( this.results.primary.length ) {
				this.mapbox.fitBounds( this.getFitBounds( this.results.primary ), fitBoundsOpts );
			} else if ( this.results.local.length ) {
				this.mapbox.fitBounds( this.getFitBounds( this.results.local ), fitBoundsOpts );
			} else {
				// no results within threshold - find closest one to fit bounds
				this.mapbox.fitBounds( this.getFitBounds( [ this.data.reduce( ( closest, item ) => item.distance < closest.distance ? item : closest, this.data[0] ) ] ), fitBoundsOpts );
			}
		}

		if ( ! this.geoMarker ) {
			this.geoMarker = new mapboxgl.Marker().setLngLat( [ this.geo.longitude, this.geo.latitude ] ).addTo( this.mapbox );
		} else {
			this.geoMarker.setLngLat( [ this.geo.longitude, this.geo.latitude ] );
		}
	}

	getFitBounds( features ) {
		return [
			features.reduce( ( min, cur ) => Math.min( min, parseFloat( cur.longitude || 0 ) ), parseFloat( this.geo.longitude ) ),
			features.reduce( ( min, cur ) => Math.min( min, parseFloat( cur.latitude || 0 ) ), parseFloat( this.geo.latitude ) ),
			features.reduce( ( max, cur ) => Math.max( max, parseFloat( cur.longitude || 0 ) ), parseFloat( this.geo.longitude ) ),
			features.reduce( ( max, cur ) => Math.max( max, parseFloat( cur.latitude || 0 ) ), parseFloat( this.geo.latitude ) ),
		];
	}

	renderResult( data, className ) {
		const result = document.createElement('article');
		result.classList.add('member-locator__result');
		result.dataset.id = data.id;

		if ( className ) {
			className.trim().split(' ').filter( cn => cn ).forEach( cn => result.classList.add(cn) );
		}

		if ( data.distance ) {
			const distance = document.createElement('p');
			distance.classList.add('member-locator__distance');

			const fixedDistance = parseFloat( data.distance.toFixed(1) ) + ' ';
			const label = document.createElement('span');
			label.classList.add('member-locator__distance__label');
			label.innerHTML = 1 === fixedDistance ? 'mile' : 'miles';;

			distance.appendChild( document.createTextNode( fixedDistance ) );
			distance.appendChild( label );
			result.appendChild(distance);



		}

		if ( data.title ) {
			const title = document.createElement('h2');
			title.classList.add( 'has-h4-variant', 'member-locator__name' );

			const link = document.createElement('a');
			link.href = data.link;

			link.appendChild( document.createTextNode( data.title ) );
			title.appendChild( link );
			result.appendChild( title );
		}

		if ( data.state_name || data.state ) {
			const state = document.createElement('p');
			state.classList.add('member-locator__state');

			state.appendChild( document.createTextNode( `${data.state_name}` ) );
			result.appendChild( state );
		}

		if ( data.formatted_address ) {
			const address = document.createElement('address');

			address.classList.add('member-locator__address');
			address.appendChild( document.createTextNode( data.formatted_address ) );
			result.appendChild( address );
		}

		return result;
	}

	renderNotice( text, type ) {
		const notice = document.createElement( 'p' );
		notice.classList.add('member-locator__notice');

		if ( type ) {
			notice.classList.add(`member-locator__notice--${type}`);
		}

		notice.appendChild( document.createTextNode( text ) );

		return notice;
	}

	autocomplete() {
		this.form.classList.add( 'is-loading' );

		fetch( `${ this.api }${ this.input.value }.json?${ new URLSearchParams( this.mapboxParams ).toString() }` ).then( (response) => {
			return response.json();
		}).then((body) => {
			this.autocompleteData = body;

			this.awesomplete.list = this.autocompleteData.features.map( feature => feature.place_name );
			this.awesomplete.evaluate();

			return this.autocompleteData;
		}).catch( error => {
			console.error(error);
		}).finally(() => {
			this.form.classList.remove( 'is-loading' );
		});
	}

	submit() {
		const value = this.input.value.trim().toLowerCase();
		let feature;

		if ( statesNameMap[ value ] || statesAbbrMap[ value ] ) {
			this.stateData  = statesNameMap[ value ] || statesAbbrMap[ value ];
			this.searchType = 'state';

			if ( this.stateData.center && this.stateData.center.length > 1 ) {
				this.geo.longitude = this.stateData.center[0];
				this.geo.latitude  = this.stateData.center[1];
			}
		} else if ( this.input.value.match( /^\d{5}(?:[-\s]\d{4})?$/ ) ) {
			this.searchType = 'zip';
		} else {
			this.searchType = null;
		}

		if ( 'state' === this.searchType && this.stateData.bbox ) {
			return this.render();
		}

		if ( this.autocompleteData ) {
			feature = this.autocompleteData.features.find( feature => feature.place_name === this.input.value );

			if ( feature ) {
				this.setGeoFromFeature( feature ).then( () => {
					this.render();
				});
				return;
			}
		}

		this.form.classList.add( 'is-loading' );

		fetch( `${this.api}${ this.input.value }.json?${ new URLSearchParams( this.mapboxParams ).toString() }` ).then( (response) => {
			return response.json();
		}).then((body) => {
			if ( body.features.length ) {
				this.setGeoFromFeature( body.features[0] ).then( () => this.render() );
			}
		}).catch( error => {
			console.error(error);
		}).finally(() => {
			this.form.classList.remove( 'is-loading' );
		});
	}

	setGeoFromFeature( feature, query = true ) {
		return new Promise( (resolve, reject) => {
			if ( feature ) {
				let postalContext = feature.context ? feature.context.find( context => context.id.startsWith('postcode.') ) : null;

				if ( postalContext ) {
					this.geo = {
						longitude: feature.center[0],
						latitude: feature.center[1],
						zip_code: postalContext ? postalContext.text : null,
					}

					resolve( this.geo );
				}

				if ( query ) {
					// if the mapbox feature doesn't have a postal code context, go a geocoding call to get the zip code
					fetch( `${ this.api }${ feature.center.join(',') }.json?${ new URLSearchParams( { ...this.mapboxParams, limit: 1 } ).toString() }` ).then( (response) => {
						return response.json();
					}).then((body) => {
						if ( body.features.length ) {
							this.setGeoFromFeature( body.features[0], false ).then( () => {
								resolve( this.geo );
							}).catch( reject );
						}
					}).catch( error => {
						console.error(error);
						reject( error );
					}).finally(() => {
						this.form.classList.remove( 'is-loading' );
					});
				} else {
					reject();
				}
			}
		});
	}
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll( '.member-locator' ).forEach( node => {
		let config = {};

		try {
			const configNode = node.querySelector('script[type="application/json"]');

			if ( configNode ) {
				config = JSON.parse( configNode.innerHTML );
			}
		} catch {

		}

		new MemberLocator( node, config );
	} );
});
